<script lang="ts">
  import { formatDelay, type DelayBadgeView } from '../../lib/delay';
  import { parseHHMMMinutes, toHHMM } from '../../lib/hhmm';
  import { formatWalkMeters, walkLabel } from '../../lib/leg-format';
  import { lineColor } from '../../lib/mode-colors';
  import type { JourneyJson } from '../../types/JourneyJson';
  import type { LegJson } from '../../types/LegJson';
  import type { StopJson } from '../../types/StopJson';
  import { SvelteSet } from 'svelte/reactivity';
  import type { BoardingHintJson } from '../../types/BoardingHintJson';
  import DelayBadge from '../delay-badge.svelte';
  import LineBadge from '../line-badge.svelte';
  import StopChip from './stop-chip.svelte';

  interface Properties {
    journey: JourneyJson;
    /** Fires when the user clicks a stop name in the Journey timeline. Parent
     *  should switch to the Departures tab and load that stop's board. */
    onOpenStopDepartures?: (stop: StopJson) => void;
  }

  const { journey, onOpenStopDepartures }: Properties = $props();

  type PaneId = 'journey' | 'hints';
  /** Currently-visible detail pane. Component-local — the parent wraps every
   *  JourneyDetail in a `{#key journey}` block so remounts (triggered whenever the
   *  parent hands a different journey identity in) reset the selection here to
   *  `journey`, even when the card was reused because `journeyKey` happened to match
   *  a prior card's key across a re-search. */
  const paneState = $state<{ active: PaneId }>({ active: 'journey' });

  function transitMinutes(transitLeg: Extract<LegJson, { type: 'transit' }>): number {
    const departureMinutes = parseHHMMMinutes(transitLeg.departure);
    const arrivalMinutes = parseHHMMMinutes(transitLeg.arrival);
    if (departureMinutes === undefined || arrivalMinutes === undefined) return 1;
    return Math.max(1, arrivalMinutes - departureMinutes);
  }

  function intermediates(transitLeg: Extract<LegJson, { type: 'transit' }>): StopJson[] {
    return transitLeg.stops.slice(1, -1);
  }

  function delayView(
    transitLeg: Extract<LegJson, { type: 'transit' }>,
  ): DelayBadgeView | undefined {
    if (transitLeg.delay_seconds === undefined) return undefined;
    return formatDelay(Number(transitLeg.delay_seconds), false, true);
  }

  /** Suppress a walking leg's "departure" row when its origin stop is the same
   *  as the previous leg's arrival — avoids "X / walk / X" duplication. */
  function shouldSkipDeparture(currentLeg: LegJson, previous: LegJson | undefined): boolean {
    if (!previous) return false;
    return previous.to.name.length > 0 && previous.to.name === currentLeg.from.name;
  }

  function walkBody(walkingLeg: Extract<LegJson, { type: 'walking' }>): string {
    const label = walkLabel(walkingLeg);
    const distance = formatWalkMeters(walkingLeg.walk_meters);
    if (walkingLeg.to.name && walkingLeg.to.name !== walkingLeg.from.name) {
      return `🚶 Walk ${label} · ${distance} to ${walkingLeg.to.name}`;
    }
    return `🚶 Walk ${label} · ${distance}`;
  }

  function waitLabel(mins: number): string {
    return `Wait ${String(mins)} min`;
  }

  const MIN_WAIT_MINUTES = 1;

  /** Minutes between the previous leg's arrival and this leg's departure, or undefined. */
  function waitMinutes(previous: LegJson | undefined, current: LegJson): number | undefined {
    if (!previous) return undefined;
    const previousArrivalMinutes = parseHHMMMinutes(previous.arrival);
    const currentDepartureMinutes = parseHHMMMinutes(current.departure);
    if (previousArrivalMinutes === undefined || currentDepartureMinutes === undefined)
      return undefined;
    const waitGap = currentDepartureMinutes - previousArrivalMinutes;
    if (waitGap < MIN_WAIT_MINUTES) return undefined;
    return waitGap;
  }

  function directLabel(mins: number): string {
    return `Direct · ${String(mins)} min`;
  }

  function stopsLabel(count: number, mins: number): string {
    const noun = count === 1 ? 'stop' : 'stops';
    return `${String(count)} ${noun} · ${String(mins)} min`;
  }

  function interId(index: number): string {
    return `inter-${String(index)}`;
  }

  /** Sticky per-user set of expanded transit legs (by index). */
  const expanded = new SvelteSet<number>();

  function toggleIntermediates(index: number): void {
    if (expanded.has(index)) expanded.delete(index);
    else expanded.add(index);
  }

  interface HintEntry {
    /** Original leg index in the journey — used for the stable list key. */
    index: number;
    /** Transit leg that carries `hint`; kept so the pane can render the
     *  line badge + boarding stop context, not just the sentence. */
    leg: Extract<LegJson, { type: 'transit' }>;
    /** Line the rider transfers to next, or `undefined` when this transit leg is
     *  followed by the last-mile walk. */
    nextLine: string | undefined;
    /** Present when the leg has a boarding hint; `undefined` renders as the
     *  "No boarding hint for this leg" placeholder. */
    hint: BoardingHintJson | undefined;
  }

  /** Every transit leg in the journey mapped to its hint entry, in leg order.
   *  `$derived` so the array is recomputed only when `journey.legs` changes.
   *  `buildNextTransitLegByLeg` runs a single reverse pass so the whole build is
   *  O(L) instead of O(L²). */
  const hintEntries = $derived<HintEntry[]>(buildHintEntries(journey.legs));

  type TransitLegJson = Extract<LegJson, { type: 'transit' }>;

  function buildHintEntries(legs: LegJson[]): HintEntry[] {
    const nextTransitLegByLeg = buildNextTransitLegByLeg(legs);
    return legs.flatMap((currentLeg, index) =>
      currentLeg.type === 'transit'
        ? [
            {
              index,
              leg: currentLeg,
              nextLine: nextTransitLegByLeg[index]?.line,
              hint: currentLeg.boarding_hint,
            },
          ]
        : [],
    );
  }

  /** Direct reference to the next transit leg after each position, or
   *  `undefined` when the position is followed only by walking legs. Single
   *  reverse pass. */
  function buildNextTransitLegByLeg(legs: LegJson[]): Array<TransitLegJson | undefined> {
    const nextLeg = Array.from<TransitLegJson | undefined>({ length: legs.length });
    let followingTransit: TransitLegJson | undefined;
    for (let index = legs.length - 1; index >= 0; index--) {
      nextLeg[index] = followingTransit;
      const currentLeg = legs[index];
      if (currentLeg.type === 'transit') followingTransit = currentLeg;
    }
    return nextLeg;
  }

  /** `0.25` → `25% from front`. Uses whole-percent rounding to match the
   *  pre-Svelte formatting. */
  function fromFrontLabel(fromFront: number): string {
    const percent = Math.round(fromFront * 100);
    return `${String(percent)}% from front`;
  }

  function hintKey(entry: HintEntry): string {
    return `hint-${String(entry.index)}`;
  }

  function selectPane(nextPane: PaneId): void {
    paneState.active = nextPane;
  }
</script>

<!-- Two panes per journey card: `Journey` (timeline of legs) and
     `Boarding hints` (per-transit-leg list). Boarding hints used to be
     inlined under every transit leg; that crowded the timeline, so the
     migration back to the pre-Svelte tab UX puts them behind an explicit
     click. -->
<section class="detail" aria-label="Journey details">
  <div class="detail-tabs" role="tablist">
    <button
      type="button"
      role="tab"
      class="detail-tab"
      class:active={paneState.active === 'journey'}
      aria-selected={paneState.active === 'journey'}
      aria-controls="pane-journey"
      onclick={() => {
        selectPane('journey');
      }}
    >
      Journey
    </button>
    <button
      type="button"
      role="tab"
      class="detail-tab"
      class:active={paneState.active === 'hints'}
      aria-selected={paneState.active === 'hints'}
      aria-controls="pane-hints"
      onclick={() => {
        selectPane('hints');
      }}
    >
      Boarding hints
    </button>
  </div>

  {#if paneState.active === 'journey'}
    <div id="pane-journey" role="tabpanel" aria-label="Journey timeline">
      <ol class="tl">
        {#each journey.legs as leg, index (`${leg.type}-${leg.departure}-${leg.arrival}-${String(index)}`)}
          {@const previous = index > 0 ? journey.legs[index - 1] : undefined}
          {@const isLast = index === journey.legs.length - 1}
          {@const wait = waitMinutes(previous, leg)}
          {#if wait !== undefined}
            <li class="tl-wait" aria-label="Wait">{waitLabel(wait)}</li>
          {/if}
          {#if leg.type === 'walking'}
            <li
              class="tl-leg tl-leg-walk"
              style="

--line-color: var(--color-fg-muted)"
            >
              {#if !shouldSkipDeparture(leg, previous)}
                <div class="tl-row">
                  <span class="tl-time">{toHHMM(leg.departure)}</span>
                  <StopChip stop={leg.from} fallback="Origin" onOpen={onOpenStopDepartures} />
                </div>
              {/if}
              <div class="tl-body tl-body-walk">{walkBody(leg)}</div>
              {#if isLast}
                <div class="tl-row">
                  <span class="tl-time">{toHHMM(leg.arrival)}</span>
                  <StopChip stop={leg.to} fallback="Destination" onOpen={onOpenStopDepartures} />
                </div>
              {/if}
            </li>
          {:else}
            {@const delayInfo = delayView(leg)}
            {@const inter = intermediates(leg)}
            {@const mins = transitMinutes(leg)}
            {@const isOpen = expanded.has(index)}
            <li
              class="tl-leg tl-leg-transit"
              style="

--line-color: {lineColor(leg)}"
            >
              <div class="tl-row">
                <span class="tl-time">{toHHMM(leg.departure)}</span>
                <StopChip stop={leg.from} fallback="" onOpen={onOpenStopDepartures} />
                {#if delayInfo}<DelayBadge view={delayInfo} />{/if}
              </div>
              <div class="tl-body">
                <div class="tl-headsign">
                  <LineBadge name={leg.line} color={lineColor(leg)} />
                  {#if leg.headsign}<span class="tl-direction">→ {leg.headsign}</span>{/if}
                </div>
                {#if inter.length === 0}
                  <div class="tl-ride-meta">{directLabel(mins)}</div>
                {:else}
                  <button
                    type="button"
                    class="tl-intermediates-toggle"
                    aria-expanded={isOpen}
                    aria-controls={interId(index)}
                    onclick={(event) => {
                      event.stopPropagation();
                      toggleIntermediates(index);
                    }}
                  >
                    {stopsLabel(inter.length, mins)}
                  </button>
                  {#if isOpen}
                    <ol
                      id={interId(index)}
                      class="tl-intermediates"
                      aria-label="Intermediate stops"
                    >
                      {#each inter as interStop, interIndex (`${String(interIndex)}|${interStop.name}`)}
                        <li class="tl-intermediate">
                          <StopChip stop={interStop} fallback="" onOpen={onOpenStopDepartures} />
                        </li>
                      {/each}
                    </ol>
                  {/if}
                {/if}
              </div>
              <div class="tl-row">
                <span class="tl-time">{toHHMM(leg.arrival)}</span>
                <StopChip stop={leg.to} fallback="" onOpen={onOpenStopDepartures} />
              </div>
            </li>
          {/if}
        {/each}
      </ol>
    </div>
  {:else}
    {@const entries = hintEntries}
    <div id="pane-hints" class="hints" role="tabpanel" aria-label="Boarding hints">
      {#if entries.length === 0}
        <p class="hints-empty">No transit legs in this journey.</p>
      {:else}
        <ol class="hints-list">
          {#each entries as entry (hintKey(entry))}
            <li class="hints-leg" aria-label="Boarding hint">
              <h4 class="hints-heading">
                <LineBadge name={entry.leg.line} color={lineColor(entry.leg)} />
                <span class="hints-route">
                  {entry.leg.from.name} → {entry.leg.to.name} → {#if entry.nextLine}{entry.nextLine}{:else}exit{/if}
                </span>
              </h4>
              {#if entry.hint}
                <p class="hints-sentence">{entry.hint.sentence}</p>
                <p class="hints-detail">
                  Position: <code>{entry.hint.position}</code>
                  <span class="hints-sep">·</span>
                  {fromFrontLabel(entry.hint.from_front)}
                  <span class="hints-sep">·</span>
                  Walk to exit: {String(entry.hint.walk_seconds)}s
                </p>
                <p class="hints-reason">{entry.hint.reason}</p>
              {:else}
                <p class="hints-missing">No boarding hint for this leg.</p>
              {/if}
            </li>
          {/each}
        </ol>
      {/if}
    </div>
  {/if}
</section>

<style>
  .detail {
    margin-top: var(--space-sm);
    margin-bottom: var(--space-md);
    padding: var(--space-md);
    background: var(--color-surface-alt);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
  }

  .detail-tabs {
    display: flex;
    gap: var(--space-xs);
    margin-bottom: var(--space-sm);
    border-bottom: 1px solid var(--color-border);
  }

  .detail-tab {
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    color: var(--color-fg-muted);
    cursor: pointer;
    padding: var(--space-xs) var(--space-sm);
    font-size: var(--font-size-sm);
  }

  .detail-tab:hover {
    color: var(--color-fg);
  }

  .detail-tab.active {
    color: var(--color-primary);
    border-bottom-color: var(--color-primary);
  }

  .tl {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .tl-leg {
    padding-bottom: var(--space-sm);
    border-left: 3px solid var(--line-color, var(--color-border));
    padding-left: var(--space-md);
    margin-left: var(--space-xs);
  }

  .tl-wait {
    color: var(--color-warn);
    font-size: var(--font-size-xs);
    font-style: italic;
    padding: 4px 0 4px var(--space-md);
    margin-left: var(--space-xs);
    border-left: 3px dotted var(--color-warn);
    letter-spacing: 0.03em;
  }

  .tl-intermediates-toggle {
    background: none;
    border: none;
    color: var(--color-primary);
    cursor: pointer;
    padding: 0 0 0 3.5rem;
    margin-top: 2px;
    font-size: var(--font-size-xs);
    text-align: left;
  }

  .tl-intermediates-toggle:hover {
    text-decoration: underline;
  }

  .tl-row {
    display: flex;
    align-items: baseline;
    gap: var(--space-sm);
    padding: 2px 0;
  }

  .tl-time {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    min-width: 3.5rem;
    color: var(--color-fg);
  }

  .tl-body {
    padding: var(--space-xs) 0;
  }

  .tl-body-walk {
    color: var(--color-fg-muted);
    font-size: var(--font-size-sm);
    padding-left: 3.5rem;
  }

  .tl-headsign {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding-left: 3.5rem;
  }

  .tl-direction {
    color: var(--color-fg-muted);
    font-size: var(--font-size-sm);
  }

  .tl-ride-meta {
    color: var(--color-fg-muted);
    font-size: var(--font-size-xs);
    padding-left: 3.5rem;
    margin-top: 2px;
  }

  .tl-intermediates {
    list-style: none;
    padding: 0 0 0 3.5rem;
    margin: var(--space-xs) 0 0;
  }

  .tl-intermediate {
    color: var(--color-fg-muted);
    font-size: var(--font-size-sm);
    padding: 1px 0 1px var(--space-sm);
    border-left: 2px dotted var(--color-border);
    margin-left: -2px;
  }

  .hints {
    padding: var(--space-xs) 0;
  }

  .hints-empty {
    color: var(--color-fg-muted);
    font-style: italic;
    margin: 0;
  }

  .hints-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
  }

  .hints-leg {
    border-left: 3px solid var(--color-primary);
    padding: var(--space-xs) 0 var(--space-xs) var(--space-md);
  }

  .hints-heading {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    margin: 0 0 var(--space-xs);
    font-size: var(--font-size-sm);
    font-weight: 600;
  }

  .hints-route {
    color: var(--color-fg);
  }

  .hints-sentence {
    font-weight: 600;
    margin: 0 0 var(--space-xs);
    color: var(--color-fg);
  }

  .hints-detail {
    color: var(--color-fg-muted);
    font-size: var(--font-size-xs);
    margin: 0 0 var(--space-xs);
  }

  .hints-sep {
    margin: 0 var(--space-xs);
  }

  .hints-reason {
    color: var(--color-fg-muted);
    font-size: var(--font-size-xs);
    font-style: italic;
    margin: 0;
  }

  .hints-missing {
    color: var(--color-fg-muted);
    font-style: italic;
    margin: 0;
  }
</style>
